import React from 'react';
import { 
  ArrowRight, 
  Shield, 
  Heart, 
  Users, 
  Award, 
  Lock, 
  Database, 
  FileText, 
  Star 
} from 'lucide-react';

interface LandingPageProps {
  onGetStarted: () => void;
  onNavigate?: (section: string) => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted, onNavigate }) => {
  const features = [
    {
      icon: Heart,
      title: "Comprehensive Condition Tracking",
      description: "Track 1000+ medical conditions with ICD-10 codes. Monitor symptoms, treatments, and progress with professional-grade accuracy."
    },
    {
      icon: Shield,
      title: "Enterprise-Grade Security",
      description: "HIPAA-compliant platform with AES-256 encryption, SOC 2 certification, and granular privacy controls for your health data."
    },
    {
      icon: Users,
      title: "Provider Collaboration",
      description: "Seamlessly share data with healthcare providers, get AI-powered insights, and coordinate care across your medical team."
    },
    {
      icon: Database,
      title: "Smart Health Analytics",
      description: "AI-powered correlation analysis, predictive insights, and personalized health recommendations based on your unique patterns."
    },
    {
      icon: FileText,
      title: "Document Intelligence",
      description: "Upload and analyze medical documents with AI. Extract key information, track trends, and organize your health records."
    },
    {
      icon: Award,
      title: "Professional Integration",
      description: "Connect with EHR systems, lab results, devices, and telehealth platforms for a complete healthcare ecosystem."
    }
  ];

  const testimonials = [
    {
      name: "Dr. Sarah Chen",
      role: "Primary Care Physician",
      content: "ZippyHealth has revolutionized how I track my patients' progress. The AI insights help me make better treatment decisions.",
      rating: 5
    },
    {
      name: "Michael Rodriguez",
      role: "Type 2 Diabetes Patient",
      content: "Finally, a platform that makes health tracking simple. The correlation analysis helped me identify my glucose triggers.",
      rating: 5
    },
    {
      name: "Dr. James Wilson",
      role: "Cardiologist",
      content: "The security and HIPAA compliance give me confidence. My patients love the provider collaboration features.",
      rating: 5
    }
  ];

  const stats = [
    { value: "1000+", label: "Medical Conditions" },
    { value: "99.9%", label: "Uptime Target" },
    { value: "256-bit", label: "Encryption Standard" },
    { value: "24/7", label: "Security Monitoring" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-100">
      {/* Mobile-friendly Navigation */}
      <nav className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 md:py-4">
            <div className="flex items-center space-x-3">
              <img 
                src="/zippy-logo-simple.svg" 
                alt="ZippyHealth Logo"
                className="w-8 h-8 md:w-10 md:h-10"
              />
              <div>
                <h1 className="text-lg md:text-xl font-bold text-slate-800">ZippyHealth.io</h1>
                <p className="text-xs text-slate-500 hidden sm:block">Comprehensive Health Platform</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 md:space-x-4">
              <button 
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-slate-600 hover:text-slate-800 font-medium hidden md:block"
              >
                Features
              </button>
              <button 
                onClick={() => document.getElementById('security')?.scrollIntoView({ behavior: 'smooth' })}
                className="text-slate-600 hover:text-slate-800 font-medium hidden md:block"
              >
                Security
              </button>
              <button 
                onClick={() => onNavigate?.('pricing')}
                className="text-slate-600 hover:text-slate-800 font-medium"
              >
                Pricing
              </button>
              <button 
                onClick={onGetStarted}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 md:px-6 md:py-2 rounded-lg text-sm md:text-base font-medium transition-all transform hover:scale-105"
              >
                Sign In
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 md:pt-20 pb-10 md:pb-16">
          <div className="text-center">
            <div className="flex justify-center mb-4 md:mb-6">
              <div className="flex items-center space-x-1 md:space-x-2 bg-red-50 border border-red-200 rounded-full px-3 py-1 md:px-4 md:py-2">
                <Shield className="h-4 w-4 text-red-600" />
                <span className="text-xs md:text-sm font-medium text-red-700">HIPAA Compliant* (pending)</span>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-4 md:mb-6 leading-tight">
              Your Health,
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-800"> Comprehensive</span>
              <br />& Secure
            </h1>
            <p className="text-lg md:text-xl text-slate-600 mb-6 md:mb-8 max-w-3xl mx-auto leading-relaxed">
              The most comprehensive, secure health tracking platform trusted by healthcare professionals. 
              Track 1000+ medical conditions with enterprise-grade security and intelligent insights.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
              <button 
                onClick={onGetStarted}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 md:px-8 md:py-4 rounded-xl font-semibold text-base md:text-lg flex items-center justify-center space-x-2 transition-all transform hover:scale-105 hover:shadow-xl"
              >
                <span>Try Demo (No Signup)</span>
                <ArrowRight className="h-5 w-5" />
              </button>
              <button 
                onClick={() => {
                  // First show users what they'll get
                  if (window.confirm('Demo includes:\n• Sample health data\n• All features unlocked\n• Data persists locally\n• No account required\n\nStart exploring now?')) {
                    onGetStarted();
                  }
                }}
                className="border-2 border-slate-300 hover:border-slate-400 text-slate-700 px-6 py-3 md:px-8 md:py-4 rounded-xl font-semibold text-base md:text-lg transition-all hover:bg-slate-50"
              >
                See What's Included
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl lg:text-5xl font-bold text-red-600 mb-1 md:mb-2">{stat.value}</div>
                <div className="text-sm md:text-base text-slate-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 md:mb-4">
              Everything You Need for Complete Health Management
            </h2>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto">
              From symptom tracking to medication management, we provide professional-grade tools 
              with the security healthcare demands.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-5 md:p-8 border border-slate-200 hover:shadow-xl transition-all duration-300 group">
                  <div className="w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-2xl flex items-center justify-center mb-4 md:mb-6 group-hover:scale-110 transition-transform">
                    <Icon className="h-6 w-6 md:h-8 md:w-8 text-white" />
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2 md:mb-3">{feature.title}</h3>
                  <p className="text-sm md:text-base text-slate-600 leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Medical Conditions Showcase */}
      <section className="bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Database className="h-6 w-6" />
                <span className="text-red-200 font-medium">Comprehensive Database</span>
              </div>
              <h2 className="text-4xl font-bold mb-6">
                1000+ Medical Conditions at Your Fingertips
              </h2>
              <p className="text-xl text-red-100 mb-8">
                Our extensively researched database covers the top medical conditions across all major categories, 
                complete with ICD-10 codes and professional medical terminology.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-2xl font-bold">15+</div>
                  <div className="text-red-200">Medical Categories</div>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-2xl font-bold">ICD-10</div>
                  <div className="text-red-200">Coded Conditions</div>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-2xl p-8">
              <h3 className="text-xl font-semibold mb-6">Popular Categories</h3>
              <div className="space-y-4">
                {[
                  { category: "Cardiovascular", count: "150+" },
                  { category: "Endocrine", count: "80+" },
                  { category: "Mental Health", count: "120+" },
                  { category: "Respiratory", count: "90+" },
                  { category: "Musculoskeletal", count: "110+" },
                  { category: "Neurological", count: "95+" }
                ].map((item, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-white">{item.category}</span>
                    <span className="text-red-200 font-medium">{item.count} conditions</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section id="security" className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center">
                <Shield className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Enterprise-Grade Security You Can Trust
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Built with healthcare in mind, our platform meets the highest security standards 
              to protect your most sensitive information.
            </p>
            <p className="text-sm text-slate-500 mt-4">
              *Certifications pending - we are actively pursuing full compliance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 text-center border border-slate-200">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Lock className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">AES-256 Encryption</h3>
              <p className="text-slate-600">End-to-end encryption ensures your data is protected at rest and in transit.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center border border-slate-200">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Award className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">SOC 2 Certified* (pending)</h3>
              <p className="text-slate-600">Independently verified security controls and compliance standards.</p>
            </div>
            <div className="bg-white rounded-2xl p-8 text-center border border-slate-200">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                <FileText className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">HIPAA Compliant* (pending)</h3>
              <p className="text-slate-600">Full compliance with healthcare privacy regulations and audit trails.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Trusted by Healthcare Professionals
            </h2>
            <p className="text-xl text-slate-600">
              See what doctors and patients are saying about our platform.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 border border-slate-200 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-slate-600 mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center text-white font-bold text-lg">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div className="ml-4">
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-slate-600 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-red-600 to-red-800 text-white py-20">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Take Control of Your Health?
          </h2>
          <p className="text-xl text-red-100 mb-8">
            Join thousands of patients and healthcare providers who trust our platform 
            for secure, comprehensive health tracking.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={onGetStarted}
              className="bg-white text-red-600 hover:bg-red-50 px-8 py-4 rounded-xl font-semibold text-lg flex items-center justify-center space-x-2 transition-all transform hover:scale-105"
            >
              <span>Get Started Free</span>
              <ArrowRight className="h-5 w-5" />
            </button>
            <button className="border-2 border-white/30 hover:border-white/50 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all hover:bg-white/10">
              Schedule Demo
            </button>
          </div>
          <p className="text-red-200 text-sm mt-6">
            Free 30-day trial • No credit card required • Pursuing HIPAA compliance*
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="/zippy-logo-simple.svg" 
                  alt="ZippyHealth Logo"
                  className="w-8 h-8"
                />
                <span className="text-xl font-bold">ZippyHealth.io</span>
              </div>
              <p className="text-slate-400 mb-4">
                Comprehensive, secure health tracking for the modern healthcare ecosystem.
              </p>
              <div className="flex items-center space-x-2 text-sm text-slate-400">
                <Shield className="h-4 w-4" />
                <span>HIPAA Compliant* • SOC 2 Certified* (pending)</span>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Platform</h3>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white transition-colors">Features</button></li>
                <li><button onClick={() => document.getElementById('security')?.scrollIntoView({ behavior: 'smooth' })} className="hover:text-white transition-colors">Security</button></li>
                <li><button onClick={() => onNavigate?.('pricing')} className="hover:text-white transition-colors">Pricing</button></li>
                <li><button onClick={() => onNavigate?.('about')} className="hover:text-white transition-colors">API Docs</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Resources</h3>
              <ul className="space-y-2 text-slate-400">
                <li><button onClick={() => onNavigate?.('contact')} className="hover:text-white transition-colors">Help Center</button></li>
                <li><button onClick={() => onNavigate?.('privacy-policy')} className="hover:text-white transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => onNavigate?.('contact')} className="hover:text-white transition-colors">Terms of Service</button></li>
                <li><button onClick={() => onNavigate?.('contact')} className="hover:text-white transition-colors">Contact</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-slate-400">
                <li>support@zippyhealth.io</li>
                <li>1-800-ZIPPY-HEALTH</li>
                <li>Available 24/7</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 text-center text-slate-400">
            <p>&copy; 2024 ZippyHealth.io. All rights reserved. Comprehensive health platform with enterprise-grade security.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
